import { useMemo } from 'react';

import { PlusIcon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { MODAL_AI_AGENT_CREATE } from '@/components/organisms/Modals/ModalConductor';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { useCreateCampaign } from '@/modules/campaigns/hooks/useCreateCampaign';
import { pushModal } from '@/redux/modals/actions';

type QuickActionsGroupProps = {
  onAction: (action: () => void, navigateTo?: { path: string; label: string }) => void;
  /** When provided, appended to each action label (e.g. the search query) */
  querySuffix?: string;
  /** When true, bypass cmdk filtering so items always render (used in no-results state) */
  forceMount?: boolean;
  /** When provided, renders a "Search conversations" item that enters the conversation drill-down */
  onConversationSearch?: () => void;
};

export const QuickActionsGroup = ({
  onAction,
  querySuffix,
  forceMount,
  onConversationSearch,
}: QuickActionsGroupProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { createCampaign } = useCreateCampaign();

  const trimmed = querySuffix?.trim();
  const suffix = trimmed ? ` "${trimmed}"` : '';
  const synonyms = t('command_palette.synonyms_new');

  // Carry the typed name to the KB create sidebar via query params. openSidebar on
  // the collections page reads `name` and sets it atomically (setSidebarMeta alone
  // gets wiped when the sidebar content is set).
  // TODO: Extract useCreateCollection hook to mirror useCreateCampaign pattern
  // (avoids query param coupling and scales better if KB create logic expands).
  const kbPath = useMemo(
    () =>
      trimmed
        ? `/knowledge-bases?action=create&name=${encodeURIComponent(trimmed)}`
        : '/knowledge-bases?action=create',
    [trimmed]
  );

  const handleCreateAgent = () => {
    dispatch(pushModal(MODAL_AI_AGENT_CREATE, { defaultName: trimmed }));
  };

  return (
    <CommandGroup heading={t('command_palette.actions')} forceMount={forceMount}>
      {onConversationSearch && (
        <CommandItem value="action-search-conversations" onSelect={onConversationSearch}>
          <SearchIcon className="size-3" />
          <span>
            {trimmed
              ? t('command_palette.search_conversations_for', { 0: trimmed })
              : t('command_palette.search_conversations')}
          </span>
        </CommandItem>
      )}
      <CommandItem
        value="action-create-ai-agent"
        keywords={[synonyms]}
        onSelect={() => onAction(handleCreateAgent)}
      >
        <PlusIcon className="size-3" />
        <span>
          {t('command_palette.create_ai_agent')}
          {suffix}
        </span>
      </CommandItem>
      <CommandItem
        value="action-create-outreach"
        keywords={[synonyms]}
        onSelect={() => onAction(() => createCampaign('marketing', trimmed))}
      >
        <PlusIcon className="size-3" />
        <span>
          {t('command_palette.create_outreach')}
          {suffix}
        </span>
      </CommandItem>
      <CommandItem
        value="action-create-recovery"
        keywords={[synonyms]}
        onSelect={() => onAction(() => createCampaign('payment_recovery', trimmed))}
      >
        <PlusIcon className="size-3" />
        <span>
          {t('command_palette.create_recovery')}
          {suffix}
        </span>
      </CommandItem>
      <CommandItem
        value="action-create-kb"
        keywords={[synonyms]}
        onSelect={() =>
          onAction(
            () => {
              // Sidebar opens on the collections page from the `action`/`name` params
            },
            { path: kbPath, label: t('command_palette.create_kb') }
          )
        }
      >
        <PlusIcon className="size-3" />
        <span>
          {t('command_palette.create_kb')}
          {suffix}
        </span>
      </CommandItem>
    </CommandGroup>
  );
};
